/* eslint-disable */
import React, { useState, useEffect, useMemo } from 'react';
import axios from '../apis/axiosConfig';
import Layout from '../common/Layout';
import ConfirmModal from '../common/ConfirmModal';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import {
  FaTrash,
  FaFileExcel,
  FaFileCsv,
  FaChevronDown,
  FaSync,
  FaClock,
  FaCalendarAlt,
  FaUser,
  FaPhoneAlt,
  FaMapMarkerAlt
} from 'react-icons/fa';

const API_BASE = (process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000/api/v1').replace(/\/+$/, '');

const formatDate = (dateStr) => {
  if (!dateStr) return { dateOnly: 'N/A', fullFormatted: 'N/A' };
  try {
    const d = new Date(String(dateStr).replace(' ', 'T'));
    if (isNaN(d.getTime())) return { dateOnly: String(dateStr), fullFormatted: String(dateStr) };

    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();

    let hours = d.getHours();
    const minutes = String(d.getMinutes()).padStart(2, '0');
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12;
    hours = hours ? hours : 12;
    const strHours = String(hours).padStart(2, '0');

    const dateOnly = `${day}/${month}/${year}`;
    const fullFormatted = `${dateOnly} ${strHours}:${minutes} ${ampm}`;
    return { dateOnly, fullFormatted };
  } catch (e) {
    return { dateOnly: String(dateStr), fullFormatted: String(dateStr) };
  }
};

const formatBookingDate = (dateStr) => {
  if (!dateStr) return 'N/A';
  try {
    const cleanStr = String(dateStr).slice(0, 10);
    const parts = cleanStr.includes('-') ? cleanStr.split('-') : cleanStr.split('/');
    if (parts.length === 3) {
      let y, m, d;
      if (parts[0].length === 4) {
        [y, m, d] = parts.map(Number);
      } else {
        [d, m, y] = parts.map(Number);
      }
      if (y && m && d) {
        const dt = new Date(y, m - 1, d);
        if (!isNaN(dt.getTime())) {
          return dt.toLocaleDateString('en-IN', {
            weekday: 'short',
            day: 'numeric',
            month: 'short',
            year: 'numeric'
          });
        }
      }
    }
    return cleanStr;
  } catch {
    return String(dateStr);
  }
};

const getLocalDateString = (d) => {
  if (!d) return '';
  const dateObj = typeof d === 'string' ? new Date(d.replace(' ', 'T')) : d;
  if (isNaN(dateObj.getTime())) return '';
  const year = dateObj.getFullYear();
  const month = String(dateObj.getMonth() + 1).padStart(2, '0');
  const day = String(dateObj.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const ArchitectureEnquiries = () => {
  const [enquiries, setEnquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [dateFilter, setDateFilter] = useState('all'); // 'today' | 'yesterday' | 'last3days' | 'all' | 'custom'
  const [customDate, setCustomDate] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showDownloadDropdown, setShowDownloadDropdown] = useState(false);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // Selection & Delete Modal
  const [selectedIds, setSelectedIds] = useState([]);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const fetchEnquiries = async () => {
    try {
      setLoading(true);
      let list = [];

      const mapEnquiryItem = (item) => {
        const msg = item.message || '';
        const slotMatch = msg.match(/Slot:\s*([^\r\n,]+)/i);
        const dateMatch = msg.match(/Date:\s*([0-9\/-]+)/i);

        let city = item.city;
        if (!city || city === '-' || city === 'Website' || city === 'N/A') {
          const matches = [...msg.matchAll(/City:\s*([^\r\n,]+)/gi)];
          const validCity = matches.map((m) => m[1].trim()).find((c) => c && c !== 'N/A' && c !== 'Website');
          city = validCity || 'Bengaluru';
        }

        const bookingDate = item.booking_date
          ? String(item.booking_date).slice(0, 10)
          : dateMatch
            ? dateMatch[1].trim()
            : item.scheduled_date
              ? String(item.scheduled_date).slice(0, 10)
              : item.date_created
                ? String(item.date_created).slice(0, 10)
                : '';

        let slotTime = item.slot_time || '';
        if (!slotTime && slotMatch) {
          slotTime = slotMatch[1].trim();
        }
        if (!slotTime && item.scheduled_date && item.scheduled_date.includes(' ')) {
          slotTime = item.scheduled_date.split(' ').slice(1).join(' ').trim();
        }
        if (!slotTime) slotTime = '10:00 AM';

        return {
          id: item.id,
          name: item.name || 'Anonymous',
          mobile: item.mobile || '-',
          email: item.email || '',
          city: city,
          booking_date: bookingDate,
          slot_time: slotTime,
          status: item.status || (item.seen ? 'confirmed' : 'booked'),
          message: item.message || item.notes || '',
          date_created: item.date_created || item.created_at || item.date_updated || item.scheduled_date || new Date().toISOString()
        };
      };

      // 1. Fetch from dedicated endpoint /architecture-enquiries
      let dedicatedList = [];
      try {
        const res = await axios.get(`${API_BASE}/architecture-enquiries`);
        const data = Array.isArray(res.data) ? res.data : res.data?.data || [];
        if (data.length > 0) {
          dedicatedList = data.map(mapEnquiryItem);
        }
      } catch (err) {
        // Dedicated endpoint not deployed on remote or returned error
      }

      // 2. Fetch from /enquiries (for bookings saved to main enquiries table)
      let enquiryList = [];
      try {
        const res = await axios.get(`${API_BASE}/enquiries`);
        const all = Array.isArray(res.data) ? res.data : res.data?.data || [];
        const isArchitectureLead = (item) => {
          const typeStr = String(item.type || '').toLowerCase();
          const catStr = String(item.category || '').toLowerCase();
          const msgStr = String(item.message || '').toLowerCase();
          const urlStr = String(item.url || '').toLowerCase();

          // Reject catalog downloads and dealer partner requests
          if (typeStr.includes('catalog') || typeStr.includes('partner with aristo')) {
            return false;
          }

          // Must be from anavrin architecture landing page or architecture consultation
          const fullText = `${typeStr} ${catStr} ${msgStr} ${urlStr}`;
          return (
            catStr.includes('architect') ||
            typeStr.includes('architect') ||
            urlStr.includes('anavrin') ||
            (msgStr.includes('slot:') && (msgStr.includes('date:') || fullText.includes('architect')))
          );
        };

        const archItems = all.filter(isArchitectureLead);
        if (archItems.length > 0) {
          enquiryList = archItems.map(mapEnquiryItem);
        }
      } catch (err) {
        // Failed to fetch /enquiries
      }

      // Combine both sources safely without duplicating records
      if (dedicatedList.length > 0 && enquiryList.length > 0) {
        const seenKeys = new Set(
          dedicatedList.map((item) => `${(item.mobile || '').replace(/\D/g, '')}_${item.booking_date || ''}_${item.slot_time || ''}`)
        );
        const uniqueFromEnquiries = enquiryList.filter((item) => {
          const key = `${(item.mobile || '').replace(/\D/g, '')}_${item.booking_date || ''}_${item.slot_time || ''}`;
          const sameId = dedicatedList.some((d) => String(d.id) === String(item.id));
          return !sameId && !seenKeys.has(key);
        });
        list = [...dedicatedList, ...uniqueFromEnquiries];
      } else if (dedicatedList.length > 0) {
        list = dedicatedList;
      } else {
        list = enquiryList;
      }

      // Sort newest first
      list.sort((a, b) => {
        const dateA = new Date(a.date_created || a.booking_date || 0).getTime();
        const dateB = new Date(b.date_created || b.booking_date || 0).getTime();
        return (isNaN(dateB) ? 0 : dateB) - (isNaN(dateA) ? 0 : dateA);
      });

      setEnquiries(list);
    } catch (err) {
      console.error('Failed to fetch architecture enquiries:', err);
      toast.error('Failed to load architecture enquiries');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEnquiries();
  }, []);

  // Date Filtering & Counter Calculations
  const now = new Date();
  const todayStr = useMemo(() => getLocalDateString(now), []);
  const yesterday = new Date();
  yesterday.setDate(now.getDate() - 1);
  const yesterdayStr = useMemo(() => getLocalDateString(yesterday), []);
  const threeDaysAgo = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() - 3);
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);

  const stats = useMemo(() => {
    let todayCount = 0;
    let yesterdayCount = 0;
    let last3DaysCount = 0;

    enquiries.forEach((item) => {
      const rawDate = item.booking_date || item.date_created;
      if (!rawDate) return;
      const dStr = String(rawDate).slice(0, 10);
      if (dStr === todayStr) todayCount++;
      if (dStr === yesterdayStr) yesterdayCount++;
      const d = new Date(String(rawDate).replace(' ', 'T'));
      if (!isNaN(d.getTime()) && d >= threeDaysAgo) last3DaysCount++;
    });

    return {
      today: todayCount,
      yesterday: yesterdayCount,
      last3Days: last3DaysCount,
      all: enquiries.length
    };
  }, [enquiries, todayStr, yesterdayStr, threeDaysAgo]);

  const filteredList = useMemo(() => {
    return enquiries.filter((item) => {
      // 1. Date Filter
      const rawDate = item.booking_date || item.date_created;
      const dStr = rawDate ? String(rawDate).slice(0, 10) : '';

      if (dateFilter === 'today') {
        if (dStr !== todayStr) return false;
      } else if (dateFilter === 'yesterday') {
        if (dStr !== yesterdayStr) return false;
      } else if (dateFilter === 'last3days') {
        const d = rawDate ? new Date(String(rawDate).replace(' ', 'T')) : null;
        if (!d || isNaN(d.getTime()) || d < threeDaysAgo) return false;
      } else if (dateFilter === 'custom' && customDate) {
        if (dStr !== customDate) return false;
      }

      // 2. Status Filter
      if (statusFilter !== 'all') {
        if ((item.status || 'booked').toLowerCase() !== statusFilter.toLowerCase()) return false;
      }

      // 3. Search Term
      if (!searchTerm.trim()) return true;
      const term = searchTerm.toLowerCase();
      return (
        (item.name && item.name.toLowerCase().includes(term)) ||
        (item.mobile && item.mobile.includes(term)) ||
        (item.email && item.email.toLowerCase().includes(term)) ||
        (item.city && item.city.toLowerCase().includes(term)) ||
        (item.slot_time && item.slot_time.toLowerCase().includes(term)) ||
        (item.status && item.status.toLowerCase().includes(term)) ||
        (item.booking_date && item.booking_date.includes(term))
      );
    });
  }, [enquiries, dateFilter, customDate, statusFilter, searchTerm, todayStr, yesterdayStr, threeDaysAgo]);

  // Pagination calculations
  const totalEntries = filteredList.length;
  const totalPages = Math.ceil(totalEntries / pageSize) || 1;
  const startIndex = (currentPage - 1) * pageSize;
  const currentEntries = filteredList.slice(startIndex, startIndex + pageSize);

  // Checkbox handlers
  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedIds(currentEntries.map((item) => item.id));
    } else {
      setSelectedIds([]);
    }
  };

  const handleSelectRow = (id) => {
    if (selectedIds.includes(id)) {
      setSelectedIds(selectedIds.filter((item) => item !== id));
    } else {
      setSelectedIds([...selectedIds, id]);
    }
  };

  // Delete modal handlers
  const handleOpenDeleteModal = (item) => {
    setItemToDelete(item);
    setDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!itemToDelete?.id) return;
    try {
      setDeleteLoading(true);
      await Promise.allSettled([
        axios.delete(`${API_BASE}/architecture-enquiries/${itemToDelete.id}`),
        axios.delete(`${API_BASE}/enquiries/${itemToDelete.id}`)
      ]);
      toast.success('Architecture enquiry deleted successfully');
      setEnquiries((prev) => prev.filter((item) => item.id !== itemToDelete.id));
      setSelectedIds((prev) => prev.filter((item) => item !== itemToDelete.id));
      setDeleteModalOpen(false);
      setItemToDelete(null);
    } catch (err) {
      console.error('Delete failed:', err);
      toast.error('Failed to delete enquiry');
    } finally {
      setDeleteLoading(false);
    }
  };

  // Status update
  const handleStatusChange = async (id, newStatus) => {
    try {
      await Promise.allSettled([
        axios.put(`${API_BASE}/architecture-enquiries/${id}/status`, { status: newStatus }),
        axios.put(`${API_BASE}/enquiries/${id}`, { seen: newStatus === 'confirmed' || newStatus === 'attended' ? 1 : 0 })
      ]);
      toast.success(`Status updated to ${newStatus}`);
      setEnquiries((prev) => prev.map((item) => (item.id === id ? { ...item, status: newStatus } : item)));
    } catch (err) {
      console.error('Failed to update status:', err);
      toast.error('Failed to update status');
    }
  };

  // CSV & Excel Export
  const handleExportCSV = (exportAll = false) => {
    const listToExport = exportAll ? enquiries : filteredList.length > 0 ? filteredList : enquiries;
    if (listToExport.length === 0) {
      toast.info('No records available to export');
      return;
    }
    const headers = [
      '#',
      'Client Name',
      'Mobile Number',
      'Email Address',
      'City',
      'Consultation Date',
      'Slot Time',
      'Status',
      'Date Booked',
      'Notes'
    ];
    const rows = listToExport.map((item, idx) => [
      idx + 1,
      `"${(item.name || '').replace(/"/g, '""')}"`,
      `"${item.mobile || ''}"`,
      `"${item.email || ''}"`,
      `"${item.city || 'Bengaluru'}"`,
      `"${item.booking_date || ''}"`,
      `"${item.slot_time || ''}"`,
      `"${item.status || 'booked'}"`,
      `"${formatDate(item.date_created).dateOnly}"`,
      `"${(item.message || '').replace(/"/g, '""')}"`
    ]);

    const csvContent = '\uFEFF' + [headers.join(','), ...rows.map((e) => e.join(','))].join('\r\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `Aristo_Architecture_Enquiries_${new Date().toISOString().slice(0, 10)}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    setShowDownloadDropdown(false);
    toast.success(`Excel / CSV sheet downloaded successfully (${listToExport.length} leads)!`);
  };

  return (
    <Layout>
      <ToastContainer position="top-right" autoClose={3000} />
      <div style={{ width: '100%', maxWidth: '100%', boxSizing: 'border-box' }}>
        {/* Top Header Bar */}
        <div className="d-flex flex-wrap justify-content-between align-items-center mb-4">
          <div className="d-flex align-items-center" style={{ gap: '14px' }}>
            <h1 className="h3 font-weight-bold mb-0 text-white" style={{ letterSpacing: '0.5px' }}>
              Architecture Enquiries
            </h1>
            <button
              type="button"
              onClick={fetchEnquiries}
              disabled={loading}
              className="btn btn-sm d-flex align-items-center"
              style={{
                backgroundColor: '#1b1a16',
                border: '1px solid rgba(196, 168, 130, 0.4)',
                color: '#c4a882',
                borderRadius: '8px',
                padding: '7px 14px',
                gap: '8px',
                fontSize: '12px',
                fontWeight: 600,
                cursor: loading ? 'not-allowed' : 'pointer'
              }}
              title="Refresh Enquiries"
            >
              <FaSync className={loading ? 'fa-spin' : ''} />
              <span>{loading ? 'Refreshing...' : 'Refresh'}</span>
            </button>
          </div>

          {/* Download Dropdown */}
          <div style={{ position: 'relative' }}>
            <button
              type="button"
              onClick={() => setShowDownloadDropdown(!showDownloadDropdown)}
              style={{
                backgroundColor: '#c4a882',
                color: '#080807',
                border: 'none',
                borderRadius: '8px',
                padding: '8px 18px',
                fontSize: '13px',
                fontWeight: 700,
                letterSpacing: '0.3px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                cursor: 'pointer',
                boxShadow: '0 4px 15px rgba(196,168,130,0.3)'
              }}
            >
              <FaFileExcel size={14} />
              <span>Download</span>
              <FaChevronDown
                size={11}
                style={{ transition: 'transform 0.2s', transform: showDownloadDropdown ? 'rotate(180deg)' : 'none' }}
              />
            </button>

            {showDownloadDropdown && (
              <div
                style={{
                  position: 'absolute',
                  right: 0,
                  top: 'calc(100% + 6px)',
                  backgroundColor: '#141310',
                  border: '1px solid rgba(196, 168, 130, 0.35)',
                  borderRadius: '10px',
                  boxShadow: '0 12px 30px rgba(0,0,0,0.8)',
                  zIndex: 9999,
                  minWidth: '180px',
                  overflow: 'hidden'
                }}
              >
                <button
                  type="button"
                  onClick={() => {
                    handleExportCSV(false);
                    setShowDownloadDropdown(false);
                  }}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    textAlign: 'left',
                    backgroundColor: 'transparent',
                    border: 'none',
                    borderBottom: '1px solid rgba(255,255,255,0.06)',
                    color: '#ffffff',
                    fontSize: '13px',
                    fontWeight: 500,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    cursor: 'pointer'
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = 'rgba(196,168,130,0.18)')}
                  onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                >
                  <FaFileExcel color="#c4a882" size={14} /> Download Excel
                </button>
                <button
                  type="button"
                  onClick={() => {
                    handleExportCSV(false);
                    setShowDownloadDropdown(false);
                  }}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    textAlign: 'left',
                    backgroundColor: 'transparent',
                    border: 'none',
                    color: '#ffffff',
                    fontSize: '13px',
                    fontWeight: 500,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    cursor: 'pointer'
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = 'rgba(196,168,130,0.18)')}
                  onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                >
                  <FaFileCsv color="#c4a882" size={14} /> Download CSV
                </button>
              </div>
            )}
          </div>
        </div>

        {/* 4 Date-based Filter Cards (Responsive Grid matching ConsultantEnquiries) */}
        {/* 4 Date-based Filter Cards */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
            gap: '14px',
            marginBottom: '24px',
            width: '100%'
          }}
        >
          {/* Card 1: Today */}
          <button
            type="button"
            onClick={() => {
              setDateFilter('today');
              setCurrentPage(1);
            }}
            style={{
              width: '100%',
              textAlign: 'left',
              backgroundColor: '#141310',
              border: dateFilter === 'today' ? '1px solid #c4a882' : '1px solid rgba(196, 168, 130, 0.2)',
              borderRadius: '12px',
              padding: '16px 18px',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              boxShadow: dateFilter === 'today' ? '0 0 15px rgba(196,168,130,0.2)' : 'none',
              display: 'block'
            }}
          >
            <div style={{ fontSize: '20px', fontWeight: 800, color: '#fff', lineHeight: 1.2 }}>
              {String(stats.today).padStart(2, '0')} <span style={{ fontSize: '13px', fontWeight: 500, color: '#aaa' }}>Bookings</span>
            </div>
            <div style={{ fontSize: '13px', fontWeight: 700, color: '#fff', marginTop: '6px' }}>Today</div>
            <div style={{ fontSize: '11px', color: '#888', marginTop: '2px' }}>{formatDate(todayStr).dateOnly}</div>
          </button>

          {/* Card 2: Yesterday */}
          <button
            type="button"
            onClick={() => {
              setDateFilter('yesterday');
              setCurrentPage(1);
            }}
            style={{
              width: '100%',
              textAlign: 'left',
              backgroundColor: '#141310',
              border: dateFilter === 'yesterday' ? '1px solid #c4a882' : '1px solid rgba(196, 168, 130, 0.2)',
              borderRadius: '12px',
              padding: '16px 18px',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              boxShadow: dateFilter === 'yesterday' ? '0 0 15px rgba(196,168,130,0.2)' : 'none',
              display: 'block'
            }}
          >
            <div style={{ fontSize: '20px', fontWeight: 800, color: '#fff', lineHeight: 1.2 }}>
              {String(stats.yesterday).padStart(2, '0')} <span style={{ fontSize: '13px', fontWeight: 500, color: '#aaa' }}>Bookings</span>
            </div>
            <div style={{ fontSize: '13px', fontWeight: 700, color: '#fff', marginTop: '6px' }}>Yesterday</div>
            <div style={{ fontSize: '11px', color: '#888', marginTop: '2px' }}>{formatDate(yesterdayStr).dateOnly}</div>
          </button>

          {/* Card 3: Last 3 Days */}
          <button
            type="button"
            onClick={() => {
              setDateFilter('last3days');
              setCurrentPage(1);
            }}
            style={{
              width: '100%',
              textAlign: 'left',
              backgroundColor: '#141310',
              border: dateFilter === 'last3days' ? '1px solid #c4a882' : '1px solid rgba(196, 168, 130, 0.2)',
              borderRadius: '12px',
              padding: '16px 18px',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              boxShadow: dateFilter === 'last3days' ? '0 0 15px rgba(196,168,130,0.2)' : 'none',
              display: 'block'
            }}
          >
            <div style={{ fontSize: '20px', fontWeight: 800, color: '#fff', lineHeight: 1.2 }}>
              {String(stats.last3Days).padStart(2, '0')} <span style={{ fontSize: '13px', fontWeight: 500, color: '#aaa' }}>Bookings</span>
            </div>
            <div style={{ fontSize: '13px', fontWeight: 700, color: '#fff', marginTop: '6px' }}>Last 3 Days</div>
            <div style={{ fontSize: '11px', color: '#888', marginTop: '2px' }}>
              {formatDate(threeDaysAgo).dateOnly} to {formatDate(todayStr).dateOnly}
            </div>
          </button>

          {/* Card 4: All Time / Custom Date Picker */}
          <div
            role="button"
            tabIndex={0}
            onClick={(e) => {
              if (!e.target.closest('input') && !e.target.closest('button')) {
                setDateFilter('all');
                setCustomDate('');
                setCurrentPage(1);
              }
            }}
            style={{
              backgroundColor: '#141310',
              border: dateFilter === 'all' || dateFilter === 'custom' ? '1px solid #c4a882' : '1px solid rgba(196, 168, 130, 0.2)',
              borderRadius: '12px',
              padding: '16px 18px',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              boxShadow: dateFilter === 'all' || dateFilter === 'custom' ? '0 0 15px rgba(196,168,130,0.2)' : 'none',
              display: 'block'
            }}
          >
            <div style={{ fontSize: '20px', fontWeight: 800, color: '#fff', lineHeight: 1.2 }}>
              {String(stats.all).padStart(2, '0')} <span style={{ fontSize: '13px', fontWeight: 500, color: '#aaa' }}>Bookings</span>
            </div>
            <div style={{ fontSize: '13px', fontWeight: 700, color: '#fff', marginTop: '6px' }}>
              {dateFilter === 'custom' && customDate ? 'Filtered Date' : 'All Dates'}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '4px' }}>
              <input
                type="date"
                value={customDate}
                onClick={(e) => e.stopPropagation()}
                onChange={(e) => {
                  const val = e.target.value;
                  setCustomDate(val);
                  if (val) {
                    setDateFilter('custom');
                  } else {
                    setDateFilter('all');
                  }
                  setCurrentPage(1);
                }}
                style={{
                  backgroundColor: '#080807',
                  border: dateFilter === 'custom' && customDate ? '1px solid #c4a882' : '1px solid rgba(196,168,130,0.4)',
                  color: '#c4a882',
                  fontSize: '11px',
                  borderRadius: '6px',
                  padding: '3px 6px',
                  outline: 'none',
                  cursor: 'pointer',
                  colorScheme: 'dark'
                }}
              />
              {customDate && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setCustomDate('');
                    setDateFilter('all');
                    setCurrentPage(1);
                  }}
                  style={{
                    backgroundColor: 'rgba(255,255,255,0.1)',
                    border: 'none',
                    color: '#c4a882',
                    borderRadius: '4px',
                    padding: '2px 6px',
                    fontSize: '11px',
                    cursor: 'pointer',
                    lineHeight: 1
                  }}
                  title="Clear Filter"
                >
                  ✕
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Main Table Card Container (Luxury Dark) */}
        <div
          style={{
            backgroundColor: '#12110e',
            border: '1px solid rgba(196, 168, 130, 0.25)',
            borderRadius: '16px',
            padding: '20px',
            boxShadow: '0 10px 30px rgba(0,0,0,0.5)',
            width: '100%',
            overflow: 'hidden'
          }}
        >
          {/* Top Controls: Filter status + Search Bar */}
          <div className="d-flex flex-wrap justify-content-between align-items-center mb-3" style={{ gap: '10px' }}>
            <div style={{ color: '#8a8377', fontSize: '13px' }}>
              Showing{' '}
              {dateFilter === 'today'
                ? 'Today'
                : dateFilter === 'yesterday'
                  ? 'Yesterday'
                  : dateFilter === 'last3days'
                    ? 'Last 3 Days'
                    : dateFilter === 'custom'
                      ? `Date: ${customDate}`
                      : 'All'}{' '}
              Architecture Consultations ({totalEntries})
              {dateFilter !== 'all' && (
                <button
                  type="button"
                  onClick={() => {
                    setDateFilter('all');
                    setCustomDate('');
                  }}
                  style={{
                    marginLeft: '10px',
                    background: 'none',
                    border: 'none',
                    color: '#c4a882',
                    fontSize: '12px',
                    textDecoration: 'underline',
                    cursor: 'pointer'
                  }}
                >
                  Reset Filter
                </button>
              )}
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
              {/* Status Select */}
              <select
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setCurrentPage(1);
                }}
                style={{
                  backgroundColor: '#080807',
                  border: '1px solid rgba(196, 168, 130, 0.3)',
                  borderRadius: '6px',
                  color: '#c4a882',
                  padding: '6px 12px',
                  fontSize: '13px',
                  outline: 'none',
                  cursor: 'pointer'
                }}
              >
                <option value="all">All Statuses</option>
                <option value="booked">Booked</option>
                <option value="confirmed">Confirmed</option>
                <option value="attended">Attended</option>
                <option value="cancelled">Cancelled</option>
              </select>

              {/* Search Bar */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ fontSize: '13px', color: '#ccc', fontWeight: 500 }}>Search:</span>
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setCurrentPage(1);
                  }}
                  placeholder="Name, phone, city..."
                  style={{
                    backgroundColor: '#080807',
                    border: '1px solid rgba(196, 168, 130, 0.3)',
                    borderRadius: '6px',
                    color: '#fff',
                    padding: '6px 10px',
                    fontSize: '12px',
                    outline: 'none',
                    width: '160px'
                  }}
                />
              </div>
            </div>
          </div>

          {/* Table Responsive Wrapper */}
          <div style={{ width: '100%', overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
            <table
              style={{
                width: '100%',
                borderCollapse: 'collapse',
                color: '#fff',
                fontSize: '12.5px'
              }}
            >
              <thead>
                <tr
                  style={{
                    color: '#c4a882',
                    fontSize: '11px',
                    fontWeight: 700,
                    borderBottom: '1px solid rgba(196,168,130,0.25)',
                    textAlign: 'left',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}
                >
                  <th style={{ width: '36px', padding: '10px 6px', textAlign: 'center' }}>
                    <input
                      type="checkbox"
                      onChange={handleSelectAll}
                      checked={currentEntries.length > 0 && selectedIds.length === currentEntries.length}
                      style={{ cursor: 'pointer', accentColor: '#c4a882' }}
                    />
                  </th>
                  <th style={{ width: '32px', padding: '10px 6px' }}>#</th>
                  <th style={{ minWidth: '130px', padding: '10px 8px' }}>Client Name</th>
                  <th style={{ minWidth: '115px', padding: '10px 8px' }}>Mobile Number</th>
                  <th style={{ minWidth: '115px', padding: '10px 8px' }}>Consultation Date</th>
                  <th style={{ minWidth: '95px', padding: '10px 8px' }}>Slot Time</th>
                  <th style={{ minWidth: '90px', padding: '10px 8px' }}>City</th>
                  <th style={{ minWidth: '95px', padding: '10px 8px' }}>Status</th>
                  <th style={{ minWidth: '100px', padding: '10px 8px' }}>Booked On</th>
                  <th style={{ width: '50px', padding: '10px 6px', textAlign: 'center' }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="10" className="text-center py-5" style={{ color: '#8a8377' }}>
                      <div className="spinner-border text-warning" role="status">
                        <span className="sr-only">Loading...</span>
                      </div>
                      <div className="mt-2">Loading architecture enquiries...</div>
                    </td>
                  </tr>
                ) : currentEntries.length === 0 ? (
                  <tr>
                    <td colSpan="10" className="text-center py-5" style={{ color: '#8a8377' }}>
                      <div style={{ fontSize: '16px', fontWeight: 600, color: '#c4a882', marginBottom: '4px' }}>
                        No Architecture Enquiries Found
                      </div>
                      <div style={{ fontSize: '12px', color: '#666' }}>
                        Bookings made from the architect ad landing page will appear here automatically!
                      </div>
                    </td>
                  </tr>
                ) : (
                  currentEntries.map((item, index) => {
                    const rowNumber = startIndex + index + 1;
                    const dateFormatted = formatDate(item.date_created);
                    const isSelected = selectedIds.includes(item.id);
                    const bookingDateFormatted = formatBookingDate(item.booking_date);

                    return (
                      <tr
                        key={item.id || index}
                        style={{
                          backgroundColor: isSelected ? 'rgba(196, 168, 130, 0.12)' : index % 2 === 0 ? '#161512' : '#141310',
                          borderBottom: '1px solid rgba(255, 255, 255, 0.04)'
                        }}
                      >
                        {/* Checkbox */}
                        <td style={{ padding: '10px 6px', textAlign: 'center', verticalAlign: 'middle' }}>
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => handleSelectRow(item.id)}
                            style={{ cursor: 'pointer', accentColor: '#c4a882' }}
                          />
                        </td>

                        {/* # */}
                        <td style={{ padding: '10px 6px', verticalAlign: 'middle', color: '#888', fontWeight: 600 }}>{rowNumber}</td>

                        {/* Client Name */}
                        <td style={{ padding: '10px 8px', verticalAlign: 'middle' }}>
                          <div style={{ fontWeight: 700, color: '#ffffff', fontSize: '13px' }}>
                            <FaUser className="mr-1 text-muted small" />
                            {item.name || 'Anonymous'}
                          </div>
                          {item.email && <div style={{ fontSize: '11px', color: '#8a8377', marginTop: '1px' }}>{item.email}</div>}
                        </td>

                        {/* Mobile Number */}
                        <td style={{ padding: '10px 8px', verticalAlign: 'middle', whiteSpace: 'nowrap' }}>
                          <a
                            href={`tel:${item.mobile}`}
                            style={{ color: '#48bb78', textDecoration: 'none', fontWeight: 500, fontSize: '12px' }}
                          >
                            <FaPhoneAlt className="mr-1 small" />
                            {item.mobile || '-'}
                          </a>
                        </td>

                        {/* Consultation Date */}
                        <td style={{ padding: '10px 8px', verticalAlign: 'middle', whiteSpace: 'nowrap' }}>
                          <span
                            style={{
                              backgroundColor: 'rgba(196, 168, 130, 0.15)',
                              border: '1px solid rgba(196, 168, 130, 0.3)',
                              color: '#c4a882',
                              padding: '3px 8px',
                              borderRadius: '6px',
                              fontSize: '11px',
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '4px'
                            }}
                          >
                            <FaCalendarAlt size={10} />
                            {bookingDateFormatted}
                          </span>
                        </td>

                        {/* Slot Time */}
                        <td style={{ padding: '10px 8px', verticalAlign: 'middle', whiteSpace: 'nowrap' }}>
                          <span
                            style={{
                              backgroundColor: 'rgba(232, 205, 148, 0.12)',
                              border: '1px solid #c9a45c',
                              color: '#e8cd94',
                              padding: '3px 8px',
                              borderRadius: '6px',
                              fontSize: '11px',
                              fontWeight: 700,
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '4px'
                            }}
                          >
                            <FaClock size={10} />
                            {item.slot_time || '10:00 AM'}
                          </span>
                        </td>

                        {/* City */}
                        <td style={{ padding: '10px 8px', verticalAlign: 'middle', color: '#ccc', fontSize: '12px', whiteSpace: 'nowrap' }}>
                          <FaMapMarkerAlt className="mr-1 text-danger small" />
                          {item.city || 'Bengaluru'}
                        </td>

                        {/* Status */}
                        <td style={{ padding: '10px 8px', verticalAlign: 'middle' }}>
                          <select
                            style={{
                              backgroundColor:
                                item.status === 'confirmed'
                                  ? '#1c4532'
                                  : item.status === 'attended'
                                    ? '#1e3a5f'
                                    : item.status === 'cancelled'
                                      ? '#4a1515'
                                      : '#423315',
                              border: '1px solid rgba(196, 168, 130, 0.3)',
                              color:
                                item.status === 'confirmed'
                                  ? '#68d391'
                                  : item.status === 'attended'
                                    ? '#63b3ed'
                                    : item.status === 'cancelled'
                                      ? '#feb2b2'
                                      : '#ecc94b',
                              borderRadius: '6px',
                              padding: '3px 6px',
                              fontSize: '11px',
                              fontWeight: 700,
                              outline: 'none',
                              cursor: 'pointer'
                            }}
                            value={item.status || 'booked'}
                            onChange={(e) => handleStatusChange(item.id, e.target.value)}
                          >
                            <option value="booked">Booked</option>
                            <option value="confirmed">Confirmed</option>
                            <option value="attended">Attended</option>
                            <option value="cancelled">Cancelled</option>
                          </select>
                        </td>

                        {/* Booked On */}
                        <td
                          style={{ padding: '10px 8px', verticalAlign: 'middle', color: '#8a8377', fontSize: '11px', whiteSpace: 'nowrap' }}
                        >
                          <div>{dateFormatted.dateOnly}</div>
                          <div style={{ fontSize: '10px', opacity: 0.8 }}>
                            {dateFormatted.fullFormatted.split(' ')[1] || ''} {dateFormatted.fullFormatted.split(' ')[2] || ''}
                          </div>
                        </td>

                        {/* Action */}
                        <td style={{ padding: '10px 6px', textAlign: 'center', verticalAlign: 'middle' }}>
                          <button
                            type="button"
                            onClick={() => handleOpenDeleteModal(item)}
                            style={{
                              background: 'none',
                              border: 'none',
                              color: '#e53e3e',
                              cursor: 'pointer',
                              padding: '4px',
                              fontSize: '13px',
                              transition: 'color 0.2s'
                            }}
                            title="Delete Booking"
                            onMouseEnter={(e) => (e.currentTarget.style.color = '#ff6b6b')}
                            onMouseLeave={(e) => (e.currentTarget.style.color = '#e53e3e')}
                          >
                            <FaTrash />
                          </button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Table Bottom: Entries Count + Pagination */}
          <div
            className="d-flex flex-wrap justify-content-between align-items-center mt-3 pt-3"
            style={{ borderTop: '1px solid rgba(255, 255, 255, 0.06)', gap: '10px' }}
          >
            <div style={{ color: '#8a8377', fontSize: '12px' }}>
              Showing {totalEntries === 0 ? 0 : startIndex + 1} to {Math.min(startIndex + pageSize, totalEntries)} of {totalEntries} entries
            </div>

            {/* Pagination Controls */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <button
                type="button"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                style={{
                  background: 'none',
                  border: 'none',
                  color: currentPage === 1 ? '#555' : '#fff',
                  cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                  padding: '6px 10px',
                  fontSize: '13px'
                }}
              >
                Previous
              </button>

              {Array.from({ length: totalPages }, (_, i) => i + 1).map((pg) => {
                const isActive = pg === currentPage;
                return (
                  <button
                    type="button"
                    key={pg}
                    onClick={() => setCurrentPage(pg)}
                    style={{
                      width: '32px',
                      height: '32px',
                      borderRadius: '6px',
                      border: 'none',
                      backgroundColor: isActive ? '#c4a882' : '#1e1c18',
                      color: isActive ? '#080807' : '#fff',
                      fontWeight: isActive ? 700 : 500,
                      fontSize: '13px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    {pg}
                  </button>
                );
              })}

              <button
                type="button"
                disabled={currentPage === totalPages || totalEntries === 0}
                onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
                style={{
                  background: 'none',
                  border: 'none',
                  color: currentPage === totalPages || totalEntries === 0 ? '#555' : '#fff',
                  cursor: currentPage === totalPages || totalEntries === 0 ? 'not-allowed' : 'pointer',
                  padding: '6px 10px',
                  fontSize: '13px'
                }}
              >
                Next
              </button>
            </div>
          </div>
        </div>

        {/* Premium Animated Delete Confirmation Modal */}
        <ConfirmModal
          isOpen={deleteModalOpen}
          type="delete"
          title="Delete Architecture Booking"
          itemName={itemToDelete?.name || 'this booking'}
          itemType="enquiry"
          message={
            itemToDelete ? (
              <>
                Are you sure you want to delete the consultation booking for{' '}
                <span className="confirm-item-highlight">"{itemToDelete.name || 'Anonymous'}"</span> on{' '}
                <span className="confirm-item-highlight">
                  {itemToDelete.booking_date ? String(itemToDelete.booking_date).slice(0, 10) : 'selected date'} at {itemToDelete.slot_time}
                </span>
                ?<div className="confirm-warning-note">This booking will be permanently removed and the slot will be released.</div>
              </>
            ) : null
          }
          confirmText="Yes, Delete"
          cancelText="Cancel"
          loading={deleteLoading}
          onConfirm={handleConfirmDelete}
          onCancel={() => {
            setDeleteModalOpen(false);
            setItemToDelete(null);
          }}
        />
      </div>
    </Layout>
  );
};

export default ArchitectureEnquiries;
